import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

class ApiService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials) {
    const response = await this.api.post('/auth/login', credentials);
    return response.data;
  }

  async register(userData) {
    const response = await this.api.post('/auth/register', userData);
    return response.data;
  }

  // User endpoints
  async getCurrentUser() {
    const response = await this.api.get('/users/me');
    return response.data;
  }

  async updateProfile(userData) {
    const response = await this.api.put('/users/me', userData);
    return response.data;
  }

  async getUsers(skip = 0, limit = 100) {
    const response = await this.api.get(`/users/?skip=${skip}&limit=${limit}`);
    return response.data;
  }

  // Project endpoints
  async getProjects() {
    const response = await this.api.get('/projects/');
    return response.data;
  }

  async createProject(projectData) {
    const response = await this.api.post('/projects/', projectData);
    return response.data;
  }

  async getProject(projectId) {
    const response = await this.api.get(`/projects/${projectId}`);
    return response.data;
  }

  async updateProject(projectId, projectData) {
    const response = await this.api.put(`/projects/${projectId}`, projectData);
    return response.data;
  }

  async deleteProject(projectId) {
    const response = await this.api.delete(`/projects/${projectId}`);
    return response.data;
  }

  async getProjectMembers(projectId) {
    const response = await this.api.get(`/projects/${projectId}/members`);
    return response.data;
  }

  async addProjectMember(projectId, memberData) {
    const response = await this.api.post(`/projects/${projectId}/members`, memberData);
    return response.data;
  }

  async removeProjectMember(projectId, userId) {
    const response = await this.api.delete(`/projects/${projectId}/members/${userId}`);
    return response.data;
  }

  // Brainstorming session endpoints
  async getSessions(projectId) {
    const response = await this.api.get(`/brainstorming/${projectId}/sessions`);
    return response.data;
  }

  async createSession(projectId, sessionData) {
    const response = await this.api.post(`/brainstorming/${projectId}/sessions`, sessionData);
    return response.data;
  }

  async getSession(projectId, sessionId) {
    const response = await this.api.get(`/brainstorming/${projectId}/sessions/${sessionId}`);
    return response.data;
  }

  // Ideas endpoints
  async getIdeas(projectId, sessionId) {
    const response = await this.api.get(`/brainstorming/${projectId}/sessions/${sessionId}/ideas`);
    return response.data;
  }

  async createIdea(projectId, sessionId, ideaData) {
    const response = await this.api.post(`/brainstorming/${projectId}/sessions/${sessionId}/ideas`, ideaData);
    return response.data;
  }

  async updateIdea(projectId, sessionId, ideaId, ideaData) {
    const response = await this.api.put(`/brainstorming/${projectId}/sessions/${sessionId}/ideas/${ideaId}`, ideaData);
    return response.data;
  }

  // Comments endpoints
  async getComments(projectId, ideaId) {
    const response = await this.api.get(`/brainstorming/${projectId}/ideas/${ideaId}/comments`);
    return response.data;
  }

  async createComment(projectId, ideaId, commentData) {
    const response = await this.api.post(`/brainstorming/${projectId}/ideas/${ideaId}/comments`, commentData);
    return response.data;
  }

  // Connections endpoints
  async getConnections(projectId, ideaId) {
    const response = await this.api.get(`/brainstorming/${projectId}/ideas/${ideaId}/connections`);
    return response.data;
  }

  async createConnection(projectId, ideaId, connectionData) {
    const response = await this.api.post(`/brainstorming/${projectId}/ideas/${ideaId}/connections`, connectionData);
    return response.data;
  }

  // File endpoints
  async uploadFile(projectId, file) {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.api.post(`/files/${projectId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getFiles(projectId) {
    const response = await this.api.get(`/files/${projectId}/files`);
    return response.data;
  }

  async askAboutFile(projectId, fileId, question) {
    const response = await this.api.post(`/files/${projectId}/files/${fileId}/ask`, { question });
    return response.data;
  }

  async deleteFile(projectId, fileId) {
    const response = await this.api.delete(`/files/${projectId}/files/${fileId}`);
    return response.data;
  }

  // Admin endpoints
  async getAllUsers() {
    const response = await this.api.get('/admin/users');
    return response.data;
  }

  async getAllProjects() {
    const response = await this.api.get('/admin/projects');
    return response.data;
  }

  async getAdminStats() {
    const response = await this.api.get('/admin/stats');
    return response.data;
  }

  async updateUser(userId, userData) {
    const response = await this.api.put(`/admin/users/${userId}`, userData);
    return response.data;
  }

  async deleteUser(userId) {
    const response = await this.api.delete(`/admin/users/${userId}`);
    return response.data;
  }

  async deleteProjectAsAdmin(projectId) {
    const response = await this.api.delete(`/admin/projects/${projectId}`);
    return response.data;
  }
}

export default new ApiService();